/**
 * 徳島県の背景ツアー「徳島めぐり」コンテンツ。
 *
 * 軸は季節と時間。春の朝の霊山寺から、冬の落合集落へ。
 *
 * 鳴門の渦潮について。
 *  兵庫県版で「鳴門の渦潮（南あわじ市）」をすでに使っている。
 *  徳島側は、大鳴門橋の橋桁空間を歩く「渦の道」として扱う。
 *  絵は「ガラス床越しに真下を見下ろす。左右は橋の巨大な鋼鉄の桁」に固定し、
 *  海上から船で見る兵庫版とは構図を完全に分ける。
 *
 * 阿波おどりについて。
 *  振付・掛け声・楽曲そのものは描写も引用もしない。
 *  阿波おどり会館という施設で毎晩公演があり、3階に展示があるという事実だけを扱う。
 *  連の名前も、会館専属連として公式に載っている「阿波の風」以外は出さない。
 *
 * 大浜海岸について。
 *  アカウミガメの産卵地。5月20日から8月20日は産卵地が午後7時30分から午前4時まで立入禁止、
 *  周辺道路は午後8時から午前4時まで車両通行禁止。フラッシュ撮影・照明撮影・花火・
 *  キャンプ・車中泊・騒音は禁止で、期間中は終日、犬の散歩もできない。
 *  だから「夜にウミガメを見に行こう」と誘う書き方はしない。砂浜と這い跡だけを描く。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - かずら橋の「日本三奇橋のひとつ」は民間団体の記述。使わない。
 *  - 脇町の重伝建選定年は文化庁の昭和63年12月16日を採る（阿波ナビの1986年とは食い違う）。
 *  - 剣山の「日本百名山」「剣山国定公園」はどちらも国・県の公式で確認できていない。書かない。
 *  - 営業時間・料金・リフトやロープウェイの運行期間は書かない。
 *  - 秋吉台と同じく、季節の開催可否は「例年」の形にとどめる。
 */

import type { StageTour } from '../types';

export const tokushimaSceneryTour: StageTour = {
  scenes: [
    {
      id: 'ryozenji',
      name: '霊山寺',
      area: '鳴門市・春の朝',
      image: '/assets/scenery/tokushima/scenes/01_ryozenji.webp',
      hotspots: [
        {
          id: 'ryozenji-ichiban',
          x: 0.46,
          y: 0.42,
          title: '遍路が始まる第一番',
          body: '竺和山一乗院霊山寺は四国八十八ヶ所霊場の第一番札所で、遍路の出発点です。発願の寺と呼ばれ、およそ1460キロの巡拝の旅はここから始まります。宗派は高野山真言宗、本尊は釈迦如来です。',
          source: 'https://88shikokuhenro.jp/01ryozenji',
          mapQuery: '霊山寺 鳴門市 大麻町板東',
        },
        {
          id: 'ryozenji-tahoto',
          x: 0.74,
          y: 0.34,
          title: '応永年間の多宝塔',
          body: '境内の多宝塔は応永年間の建立と伝わり、五智如来を祀ります。明治24年の火災を免れた本堂と多宝塔が、今に残る建物です。',
          source: 'https://88shikokuhenro.jp/01ryozenji',
          mapQuery: '霊山寺 多宝塔',
        },
        {
          id: 'ryozenji-gyoki',
          x: 0.22,
          y: 0.5,
          title: '開いたのは行基菩薩',
          body: '開基は行基菩薩と伝わります。所在地は鳴門市大麻町板東塚鼻。山号を竺和山、院号を一乗院といい、正式には竺和山一乗院霊山寺と呼ばれます。',
          source: 'https://88shikokuhenro.jp/01ryozenji',
          mapQuery: '鳴門市大麻町板東塚鼻',
        },
      ],
    },
    {
      id: 'ohama-kaigan',
      name: '大浜海岸',
      area: '美波町・初夏の夜',
      image: '/assets/scenery/tokushima/scenes/02_ohama_kaigan.webp',
      hotspots: [
        {
          id: 'ohama-tennen',
          x: 0.46,
          y: 0.46,
          title: 'アカウミガメが上陸する砂浜',
          body: '南北およそ500メートルの砂浜で、毎年5月から8月にかけてアカウミガメが産卵のために上陸します。昭和42年8月16日に国の天然記念物に指定されました。',
          source: 'https://www.awanavi.jp/archives/spot/19101',
          mapQuery: '大浜海岸 美波町 日和佐',
        },
        {
          id: 'ohama-tachiiri',
          x: 0.74,
          y: 0.38,
          title: '夜の浜は立入禁止',
          body: '5月20日から8月20日は保護規制期間です。天然記念物海亀産卵地は午後7時30分から午前4時まで立入禁止、周辺の道路は午後8時から午前4時まで車両通行禁止になります。',
          source: 'https://www.town.minami.lg.jp/docs/296.html',
          mapQuery: '大浜海岸 ウミガメ 美波町',
        },
        {
          id: 'ohama-hikari',
          x: 0.22,
          y: 0.5,
          title: '光も音も出さない',
          body: '規制期間中は、フラッシュやストロボによる写真撮影、照明を使ったビデオ撮影、花火、キャンプや車中泊、夜間の大声などの騒音が禁止されています。期間中は終日、大浜海岸での犬の散歩も遠慮するよう求められています。',
          source: 'https://www.town.minami.lg.jp/docs/296.html',
          mapQuery: '日和佐うみがめ博物館',
        },
      ],
    },
    {
      id: 'tsurugisan',
      name: '剣山',
      area: '三好市・美馬市・夏の早朝',
      image: '/assets/scenery/tokushima/scenes/03_tsurugisan.webp',
      hotspots: [
        {
          id: 'tsurugi-1955m',
          x: 0.46,
          y: 0.38,
          title: '西日本で二番目に高い山',
          body: '標高1955メートル。西日本で二番目に高い山で、晴れた日の山頂からは太平洋、瀬戸内海、紀伊半島、そして岡山県まで見渡せます。',
          source: 'https://discovertokushima.net/en/spots/tsurugi/',
          mapQuery: '剣山',
        },
        {
          id: 'tsurugi-lift',
          x: 0.74,
          y: 0.5,
          title: 'リフトで標高1750メートルまで',
          body: '登山リフトが標高1750メートルまで運んでくれ、そこから山頂までは徒歩でおよそ1時間です。運行の時期は年によって変わるので、出かける前に最新の発表を確かめてください。',
          source: 'https://discovertokushima.net/en/spots/tsurugi/',
          mapQuery: '剣山 見ノ越 登山リフト',
        },
        {
          id: 'tsurugi-mitsu',
          x: 0.22,
          y: 0.48,
          title: '三つの市町にまたがる',
          body: '美馬市木屋平、那賀郡那賀町、三好市東祖谷にまたがります。原始林が姿を残し、高山植物の群生地もあって、四季折々の変化が楽しめます。',
          source: 'https://www.pref.tokushima.lg.jp/japanese/tourism/spot/tsurugisan',
          mapQuery: '剣山 三好市東祖谷',
        },
      ],
    },
    {
      id: 'uzunomichi',
      name: '渦の道',
      area: '鳴門市・夏の大潮',
      image: '/assets/scenery/tokushima/scenes/04_uzunomichi.webp',
      hotspots: [
        {
          id: 'uzunomichi-450m',
          x: 0.46,
          y: 0.44,
          title: '橋桁の中を歩く450メートル',
          body: '大鳴門橋の橋桁空間を利用した遊歩道で、全長450メートル、海面からの高さは45メートルです。展望室にはガラス床が設けられ、真下の海を見下ろせます。',
          source: 'https://www.uzunomichi.jp/',
          mapQuery: '渦の道 鳴門市 鳴門公園',
        },
        {
          id: 'uzunomichi-danssa',
          x: 0.74,
          y: 0.34,
          title: '1.5メートルの段差が渦を生む',
          body: '鳴門海峡の幅はわずか1.3キロ。南の紀伊水道と北の播磨灘で干満の時刻が逆になるため、水面におよそ1.5メートルの段差ができ、高いほうから低いほうへ激しく流れ込みます。',
          source: 'https://www.city.naruto.tokushima.jp/manabu/kanko/uzushio/',
          mapQuery: '鳴門海峡',
        },
        {
          id: 'uzunomichi-15m',
          x: 0.22,
          y: 0.5,
          title: '渦の直径は15メートル以上',
          body: '潮流の速さは通常13キロから15キロ毎時、春秋の大潮では18キロ毎時に達します。渦は速い流れとよどみの境目にでき、南流時には直径15メートル以上になることもあります。',
          source: 'https://www.city.naruto.tokushima.jp/manabu/kanko/uzushio/',
          mapQuery: '大鳴門橋 渦潮',
        },
      ],
    },
    {
      id: 'awaodori-kaikan',
      name: '阿波おどり会館',
      area: '徳島市・夏の夜',
      image: '/assets/scenery/tokushima/scenes/05_awaodori_kaikan.webp',
      hotspots: [
        {
          id: 'kaikan-koen',
          x: 0.46,
          y: 0.42,
          title: '昼は専属連、夜は有名連',
          body: '昼は専属連「阿波の風」が1日4回、各およそ40分の公演を行い、阿波おどり体験コーナーもあります。夜は有名連が日替わりで1日1回50分の公演を行います。',
          source: 'https://www.awanavi.jp/archives/spot/19019',
          mapQuery: '阿波おどり会館 徳島市 新町橋',
        },
        {
          id: 'kaikan-museum',
          x: 0.74,
          y: 0.34,
          title: '3階は阿波おどりミュージアム',
          body: '3階の阿波おどりミュージアムでは、衣装や小道具、昔と今の鳴り物を展示しており、時代の移り変わりを知ることができます。',
          source: 'https://www.awaodori-kaikan.jp/',
          mapQuery: '阿波おどりミュージアム',
        },
        {
          id: 'kaikan-bizan',
          x: 0.22,
          y: 0.5,
          title: '5階から眉山へ',
          body: '5階が眉山ロープウェイの山麓駅で、山頂駅までおよそ6分です。1階には物産観光交流プラザがあります。所在地は徳島市新町橋2丁目20番地。',
          source: 'https://www.awanavi.jp/archives/spot/19019',
          mapQuery: '眉山ロープウェイ 山麓駅',
        },
      ],
    },
    {
      id: 'kazurabashi',
      name: '祖谷のかずら橋',
      area: '三好市・夏の夜',
      image: '/assets/scenery/tokushima/scenes/06_kazurabashi.webp',
      hotspots: [
        {
          id: 'kazura-6ton',
          x: 0.46,
          y: 0.44,
          title: 'シラクチカズラおよそ6トン',
          body: '長さ45メートル、幅2メートル、水面からの高さ14メートル。シラクチカズラおよそ6トンで編まれており、3年ごとに架け替えられます。',
          source: 'https://miyoshi-tourism.jp/spot/46/',
          mapQuery: '祖谷のかずら橋 三好市西祖谷山村善徳',
        },
        {
          id: 'kazura-minzoku',
          x: 0.74,
          y: 0.36,
          title: '国の重要有形民俗文化財',
          body: '国の重要有形民俗文化財に指定されています。橋床の隙間から谷底が見え、渡るたびに足元が揺れます。所在地は三好市西祖谷山村善徳。',
          source: 'https://miyoshi-tourism.jp/spot/46/',
          mapQuery: 'かずら橋 三好市',
        },
        {
          id: 'kazura-lightup',
          x: 0.22,
          y: 0.5,
          title: '毎晩ライトアップ',
          body: '毎日19時から21時30分までライトアップされ、谷の闇に橋だけが浮かび上がります。屋島の合戦に敗れた平国盛らがこの地に土着したと伝わる祖谷にあります。',
          source: 'https://miyoshi-tourism.jp/spot/46/',
          mapQuery: 'かずら橋夢舞台',
        },
      ],
    },
    {
      id: 'wakimachi',
      name: '脇町うだつの町並み',
      area: '美馬市・秋の夕暮れ',
      image: '/assets/scenery/tokushima/scenes/07_wakimachi.webp',
      hotspots: [
        {
          id: 'wakimachi-udatsu',
          x: 0.46,
          y: 0.42,
          title: 'うだつは防火の袖壁',
          body: 'うだつとは、隣家との境に張り出した袖壁のことです。瓦を連ねて葺き、家紋入りの鬼瓦をのせたもので、火が燃え移るのを防ぐと同時に、商人の財力を示す飾りでもありました。',
          source: 'https://www.awanavi.jp/archives/spot/19082',
          mapQuery: 'うだつの町並み 美馬市脇町南町',
        },
        {
          id: 'wakimachi-ai',
          x: 0.74,
          y: 0.34,
          title: '百を超える藍商人',
          body: '江戸時代中頃、藍の集散地として栄え、最盛期には百を超える藍商人がいました。吉野川の水運を使って藍を運び出していました。',
          source: 'https://www.awanavi.jp/archives/spot/19082',
          mapQuery: '脇町 藍商人',
        },
        {
          id: 'wakimachi-judenken',
          x: 0.22,
          y: 0.5,
          title: '商家町として国が選定',
          body: '美馬市脇町南町は商家町として、昭和63年12月16日に国の重要伝統的建造物群保存地区に選定されました。面積は5.3ヘクタールです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '美馬市脇町南町伝統的建造物群保存地区',
        },
      ],
    },
    {
      id: 'oboke',
      name: '大歩危・小歩危',
      area: '三好市・秋の紅葉',
      image: '/assets/scenery/tokushima/scenes/08_oboke.webp',
      hotspots: [
        {
          id: 'oboke-kesshohengan',
          x: 0.46,
          y: 0.44,
          title: '結晶片岩が削られた峡谷',
          body: '吉野川の流れが結晶片岩を削ってできた峡谷です。小歩危には小歩危砂岩片岩と呼ばれる岩肌があり、小歩危は大歩危の下流およそ3キロに位置します。',
          source: 'https://miyoshi-tourism.jp/spot/53/',
          mapQuery: '大歩危峡 三好市山城町',
        },
        {
          id: 'oboke-meisho',
          x: 0.74,
          y: 0.36,
          title: '国の天然記念物であり名勝',
          body: '大歩危は平成26年3月18日に国の天然記念物に、平成27年10月7日に国の名勝に指定されました。平成30年2月13日には小歩危が加わり、「大歩危小歩危」として国の名勝になっています。',
          source: 'https://miyoshi-tourism.jp/spot/53/',
          mapQuery: '小歩危 三好市',
        },
        {
          id: 'oboke-yuransen',
          x: 0.22,
          y: 0.5,
          title: '遊覧船でおよそ30分',
          body: '峡谷は遊覧船で巡ることができ、所要時間はおよそ30分です。春は桜、秋は紅葉が清流を彩ります。所在地は三好市山城町重実から上名にかけて。',
          source: 'https://miyoshi-tourism.jp/spot/53/',
          mapQuery: '大歩危峡観光遊覧船',
        },
      ],
    },
    {
      id: 'bizan',
      name: '眉山',
      area: '徳島市・秋の夜',
      image: '/assets/scenery/tokushima/scenes/09_bizan.webp',
      hotspots: [
        {
          id: 'bizan-manyoshu',
          x: 0.46,
          y: 0.42,
          title: '万葉集に詠まれた山',
          body: '標高277メートル。眉のごと雲居に見ゆる阿波の山、と万葉集に詠まれた、徳島市のシンボルです。山頂一帯は眉山公園として整備されています。',
          source: 'https://www.awanavi.jp/archives/spot/3048',
          mapQuery: '眉山公園 徳島市',
        },
        {
          id: 'bizan-awaji',
          x: 0.74,
          y: 0.34,
          title: '淡路島や紀伊半島まで',
          body: '山頂からの眺めは、徳島市街はもとより、天気のよい日には淡路島や紀伊半島まで一望できます。四季それぞれに桜と紅葉が楽しめます。',
          source: 'https://www.awanavi.jp/archives/spot/3048',
          mapQuery: '眉山 展望',
        },
        {
          id: 'bizan-ropeway',
          x: 0.22,
          y: 0.5,
          title: 'ロープウェイでおよそ6分',
          body: '阿波おどり会館の5階が眉山ロープウェイの山麓駅で、山頂駅までおよそ6分です。JR徳島駅から山麓駅までは徒歩10分ほどです。',
          source: 'https://www.awanavi.jp/archives/spot/19019',
          mapQuery: '眉山ロープウェイ',
        },
      ],
    },
    {
      id: 'ochiai-shuraku',
      name: '落合集落',
      area: '三好市東祖谷・冬',
      image: '/assets/scenery/tokushima/scenes/10_ochiai_shuraku.webp',
      hotspots: [
        {
          id: 'ochiai-390m',
          x: 0.46,
          y: 0.4,
          title: '標高差およそ390メートル',
          body: '祖谷川と落合川の合流点から山の斜面に沿って広がる集落で、集落内の標高差はおよそ390メートルに達します。',
          source: 'https://miyoshi-tourism.jp/spot/119/',
          mapQuery: '落合集落展望所 三好市東祖谷',
        },
        {
          id: 'ochiai-judenken',
          x: 0.74,
          y: 0.5,
          title: '山村集落として国が選定',
          body: '三好市東祖谷山村落合は山村集落として、平成17年12月27日に国の重要伝統的建造物群保存地区に選定されました。面積は32.3ヘクタールです。',
          source:
            'https://www.bunka.go.jp/seisaku/bunkazai/shokai/hozonchiku/judenken_ichiran.html',
          mapQuery: '三好市東祖谷山村落合伝統的建造物群保存地区',
        },
        {
          id: 'ochiai-ishigaki',
          x: 0.22,
          y: 0.48,
          title: '積み上げた石垣と畑',
          body: '急傾斜地に広がる集落に、一つひとつ積み上げた石垣と畑の光景が残ります。対岸には落合集落展望所があります。',
          source: 'https://www.pref.tokushima.lg.jp/japanese/tourism/spot/ochiaisyuraku',
          mapQuery: '落合集落 石垣',
        },
      ],
    },
  ],
};
